import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, FileText, Calendar, Mail, Phone, Building2, Globe, User, Tag } from 'lucide-react';
import { useCRMStore } from '../store/crmStore';
import { supabase } from '../lib/supabase';
import LoadingSpinner from './LoadingSpinner';

interface ContactWithNotes {
  id: string;
  first_name: string;
  last_name: string;
  company?: string;
  company_url?: string;
  email: string;
  phone?: string;
  status: 'prospect' | 'customer';
  source?: string;
  last_contact_date?: string;
  has_notes?: boolean;
}

export default function AdminCRMContacts() {
  const { 
    contacts, 
    totalContacts, 
    currentPage, 
    loading, 
    error,
    fetchContacts,
    deleteContact
  } = useCRMStore();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [contactsWithNotes, setContactsWithNotes] = useState<ContactWithNotes[]>([]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const checkContactNotes = async () => {
      const contactsData = await Promise.all(
        contacts.map(async (contact) => {
          const { data } = await supabase
            .from('crm_contact_notes')
            .select('id')
            .eq('contact_id', contact.id)
            .eq('deleted', false)
            .limit(1);
          
          return {
            ...contact,
            has_notes: data && data.length > 0
          };
        })
      );
      setContactsWithNotes(contactsData);
    };

    if (contacts.length > 0) {
      checkContactNotes();
    }
  }, [contacts]);

  const handleDelete = async (id: string) => {
    try {
      await deleteContact(id);
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
        <Link
          to="/admin/crm/contacts/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Contact
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {contactsWithNotes.map((contact) => (
            <li key={contact.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <User className="h-5 w-5 text-gray-400 mr-2" />
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        contact.status === 'customer' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {contact.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <Mail className="flex-shrink-0 mr-1.5 h-4 w-4" />
                      <a href={`mailto:${contact.email}`} className="hover:text-[#F98B3D]">
                        {contact.email}
                      </a>
                    </div>
                    {contact.phone && (
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Phone className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        <a href={`tel:${contact.phone}`} className="hover:text-[#F98B3D]">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    {contact.company && (
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Building2 className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        {contact.company}
                        {contact.company_url && (
                          <>
                            <span className="mx-1">•</span>
                            <a 
                              href={`https://${contact.company_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center hover:text-[#F98B3D]"
                            >
                              <Globe className="h-4 w-4 mr-1" />
                              Website
                            </a>
                          </>
                        )}
                      </div>
                    )}
                    {contact.source && (
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Tag className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        Source: {contact.source}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <Link
                      to={`/admin/crm/contacts/${contact.id}/notes`}
                      className={`${contact.has_notes ? 'text-[#F98B3D]' : 'text-gray-400'} hover:text-[#F98B3D]`}
                      title={contact.has_notes ? 'View Notes (Has existing notes)' : 'View Notes'}
                    >
                      <FileText className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/admin/crm/contacts/${contact.id}`}
                      className="text-gray-400 hover:text-gray-500"
                      title="Edit Contact"
                    >
                      <Edit2 className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => setShowDeleteConfirm(contact.id)}
                      className="text-red-400 hover:text-red-500"
                      title="Delete Contact"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {contact.last_contact_date && (
                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    Last Contact: {new Date(contact.last_contact_date).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm === contact.id && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Delete Contact
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Are you sure you want to delete {contact.first_name} {contact.last_name}? 
                      This action cannot be undone.
                    </p>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {contacts.length === 0 && (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new contact.
          </p>
          <div className="mt-6">
            <Link
              to="/admin/crm/contacts/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Contact
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}